import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { SHIPPING } from "@/constants/testIds";
import { Truck, Timer, PackageCheck, MapPin } from "lucide-react";

// Approx city hub positions on a 720x430 viewBox of the continental US.
const HUBS = [
  { id: "sea", city: "Seattle", eta: "2 days", x: 96, y: 96 },
  { id: "lax", city: "Los Angeles", eta: "2 days", x: 118, y: 250 },
  { id: "den", city: "Denver", eta: "1 day", x: 300, y: 205 },
  { id: "dal", city: "Dallas", eta: "1 day", x: 372, y: 300 },
  { id: "chi", city: "Chicago", eta: "1 day", x: 452, y: 150 },
  { id: "atl", city: "Atlanta", eta: "2 days", x: 540, y: 262 },
  { id: "mia", city: "Miami", eta: "2 days", x: 602, y: 350 },
  { id: "nyc", city: "New York", eta: "1 day", x: 626, y: 138 },
];

// central fulfillment hub (Memphis-ish)
const CORE = { x: 470, y: 235 };

// simplified, decorative continental US silhouette
const US_PATH =
  "M70,72 L300,58 L332,82 L358,66 L388,94 L412,70 L648,86 L666,72 L650,120 L622,150 L602,176 L592,250 L612,330 L600,362 L576,300 L470,300 L398,322 L360,372 L330,320 L150,300 L96,256 L80,210 L120,170 L86,120 Z";

const STATS = [
  { icon: Timer, big: "1–2 days", small: "Fast coast-to-coast delivery" },
  { icon: Truck, big: "Fast Shipping", small: "Reliable nationwide shipping" },
  { icon: PackageCheck, big: "Tracked", small: "Real-time tracking, every mile" },
];

export const ShippingMap = () => {
  const [hover, setHover] = useState(null);

  return (
    <section
      id="shipping"
      data-testid={SHIPPING.section}
      className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
    >
      <div className="grid items-center gap-10 lg:grid-cols-2">
        {/* Left: copy + stats */}
        <div>
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-neo-cyan">
              Fast Shipping
            </span>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Made by makers near you. Shipped fast.
            </h2>
            <p className="mt-3 max-w-md text-base text-muted-foreground">
              Orders are matched to an independent maker near you and ship with
              prepaid FedEx labels &mdash; so your prints arrive in days, tracked
              every mile of the way.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {STATS.map((s) => (
                <div
                  key={s.big}
                  data-testid={SHIPPING.stat}
                  className="rounded-2xl border border-border/70 bg-card/70 p-4 backdrop-blur-md"
                >
                  <s.icon className="h-5 w-5 text-neo-cyan" />
                  <div className="mt-2 font-display text-xl font-semibold text-foreground">
                    {s.big}
                  </div>
                  <div className="font-mono text-[11px] leading-snug text-muted-foreground">
                    {s.small}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Right: USA map card */}
        <Reveal delay={0.1} x={20} y={0}>
          <div
            data-testid={SHIPPING.map}
            className="relative overflow-hidden rounded-3xl border border-border/70 bg-card/70 p-4 shadow-[var(--shadow-elev-2)] backdrop-blur-md"
          >
            <div className="bg-grid absolute inset-0 opacity-30" aria-hidden />
            <svg viewBox="0 0 720 430" className="relative w-full">
              <defs>
                <radialGradient id="mapGlow" cx="50%" cy="45%" r="60%">
                  <stop offset="0%" stopColor="rgba(0,229,255,0.12)" />
                  <stop offset="100%" stopColor="transparent" />
                </radialGradient>
              </defs>
              <rect x="0" y="0" width="720" height="430" fill="url(#mapGlow)" />

              {/* US silhouette */}
              <path
                d={US_PATH}
                fill="rgba(255,255,255,0.03)"
                stroke="rgba(0,229,255,0.35)"
                strokeWidth="1.5"
              />

              {/* routes from core hub */}
              {HUBS.map((h) => (
                <line
                  key={"r" + h.id}
                  className="route-dash"
                  x1={CORE.x}
                  y1={CORE.y}
                  x2={h.x}
                  y2={h.y}
                  stroke="rgba(0,229,255,0.5)"
                  strokeWidth="1.5"
                />
              ))}

              {/* core hub */}
              <circle cx={CORE.x} cy={CORE.y} r="6" fill="var(--neo-magenta)" />
              <circle cx={CORE.x} cy={CORE.y} r="6" fill="none" stroke="var(--neo-magenta)" strokeWidth="1.5">
                <animate attributeName="r" from="6" to="16" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.8" to="0" dur="2s" repeatCount="indefinite" />
              </circle>

              {/* city hubs */}
              {HUBS.map((h) => {
                const isOn = hover === h.id;
                return (
                  <g
                    key={h.id}
                    data-testid={SHIPPING.hub}
                    data-city={h.city}
                    onMouseEnter={() => setHover(h.id)}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: "pointer" }}
                  >
                    <title>{`${h.city} — ${h.eta}`}</title>
                    <circle cx={h.x} cy={h.y} r={isOn ? 6 : 4.5} fill="var(--neo-cyan)" />
                    <circle cx={h.x} cy={h.y} r="4.5" fill="none" stroke="var(--neo-cyan)" strokeWidth="1.2" opacity="0.6">
                      <animate attributeName="r" from="4.5" to="13" dur="2.4s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.6" to="0" dur="2.4s" repeatCount="indefinite" />
                    </circle>
                    {isOn && (
                      <g>
                        <rect x={h.x + 10} y={h.y - 24} width="104" height="34" rx="6" fill="rgba(7,9,13,0.92)" stroke="rgba(0,229,255,0.4)" />
                        <text x={h.x + 18} y={h.y - 10} fill="#eaf6ff" fontSize="12" fontFamily="'Space Grotesk',sans-serif">{h.city}</text>
                        <text x={h.x + 18} y={h.y + 4} fill="#7fe9ff" fontSize="10" fontFamily="monospace">ETA {h.eta}</text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>

            <div className="mt-2 flex items-center gap-2 px-2 pb-1">
              <MapPin className="h-4 w-4 text-neo-cyan" />
              <span className="font-mono text-[11px] text-muted-foreground">
                Hover a hub to see delivery estimates
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
